import React from 'react';
import { 
  Modal, 
  Space, 
  Button, 
  Row, 
  Col, 
  Card, 
  Badge, 
  Typography, 
  Input, 
  Tag, 
  Divider 
} from 'antd';
import { CameraOutlined, UserOutlined, ExperimentOutlined } from '@ant-design/icons';
import { CreateScenarioImagePromptResponse } from '../../api/models/Lifetree';

const { Text } = Typography;
const { TextArea } = Input;

interface CreateScenarioImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (description: string) => Promise<void>;
  onGeneratePrompt: (description?: string) => Promise<CreateScenarioImagePromptResponse>;
  isLoading: boolean;
  generatePromptLoading: boolean;
  characters: { name: string; avatar: string | null; type: 'main' | 'npc' }[];
}

const CreateScenarioImageModal: React.FC<CreateScenarioImageModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onGeneratePrompt,
  isLoading,
  generatePromptLoading,
  characters,
}) => {
  const [description, setDescription] = React.useState<string>('');
  const [refinedDescription, setRefinedDescription] = React.useState<string>('');

  React.useEffect(() => {
    if (isOpen) {
      setDescription('');
      setRefinedDescription('');
    }
  }, [isOpen]);

  const handleCreate = async () => {
    await onSubmit(description);
  };

  const handleGeneratePromptClick = async () => {
    try {
      const response = await onGeneratePrompt(description);
      setRefinedDescription(response.refinedVisualDescription || '');
      setDescription(response.technicalPrompt || response.description);
    } catch (error) {
      // Error is handled in parent
    }
  };

  return (
    <Modal
      title={
        <Space>
          <CameraOutlined />
          Tạo ảnh Scenario
        </Space>
      }
      open={isOpen}
      onCancel={onClose}
      width={700}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={isLoading}>
          Hủy
        </Button>,
        <Button
          key="create"
          type="primary"
          onClick={handleCreate}
          loading={isLoading}
          disabled={!description.trim()}
        >
          Tạo ảnh
        </Button>,
      ]}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {/* Reference Images section */}
        <div style={{ marginBottom: 8 }}>
          <label style={{ display: 'block', marginBottom: 12, fontWeight: 'bold' }}>
            Ảnh tham chiếu (Avatar nhân vật):
          </label>
          <Row gutter={[16, 16]}>
            {characters.map((char, idx) => (
              <Col key={idx} span={8}>
                <Card 
                  size="small" 
                  bodyStyle={{ padding: '8px', textAlign: 'center' }}
                  style={{ background: '#fafafa', border: '1px solid #f0f0f0' }}
                >
                  <div style={{ position: 'relative', marginBottom: 8, width: '100%', display: 'flex', justifyContent: 'center' }}>
                    <div style={{ 
                      width: '100px', 
                      aspectRatio: '1 / 1', 
                      border: '1px solid #d9d9d9', 
                      borderRadius: '4px',
                      overflow: 'hidden',
                      background: '#f0f0f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {char.avatar ? (
                        <img src={char.avatar} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} alt={char.name} />
                      ) : (
                        <UserOutlined style={{ fontSize: '32px', color: '#d9d9d9' }} />
                      )}
                    </div>
                    <Badge 
                      count={`Image ${idx + 1}`} 
                      style={{ 
                        backgroundColor: char.type === 'main' ? '#faad14' : '#1890ff',
                        position: 'absolute',
                        top: -10,
                        right: -10
                      }} 
                    />
                  </div>
                  <Text strong ellipsis style={{ display: 'block', maxWidth: '100%' }}>
                    {char.name}
                  </Text>
                  <Tag color={char.type === 'main' ? 'gold' : 'blue'} style={{ fontSize: '10px' }}>
                    {char.type === 'main' ? 'CHÍNH' : 'NPC'}
                  </Tag>
                </Card>
              </Col>
            ))}
          </Row>
        </div>

        <Divider style={{ margin: '12px 0' }} />

        {refinedDescription && (
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold', color: '#1890ff' }}>
              Ý tưởng hình ảnh (Tiếng Việt):
            </label>
            <div style={{ 
              padding: '12px', 
              background: '#f0f5ff', 
              borderRadius: '8px', 
              border: '1px solid #adc6ff',
              marginBottom: 16,
              whiteSpace: 'pre-wrap'
            }}>
              {refinedDescription}
            </div>
          </div>
        )}

        <div>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
            Prompt kỹ thuật (English) <span style={{ color: 'red' }}>*</span>:
          </label>
          <TextArea
            placeholder="Nhập mô tả kỹ thuật chi tiết về ảnh scenario (Tiếng Anh)..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={6}
            style={{ width: '100%' }}
          />
          <div style={{ marginTop: 8, textAlign: 'right' }}>
            <Button
              type="default"
              onClick={handleGeneratePromptClick}
              loading={generatePromptLoading}
              disabled={isLoading}
              icon={<ExperimentOutlined />}
            >
              Tạo prompt bằng AI
            </Button>
          </div>
        </div>
      </Space>
    </Modal>
  );
};

export default CreateScenarioImageModal;
